import React from 'react';
import {useActionHistory} from './use-action-history';
import {
    isRebuildLayoutAction,
    isLayoutCalculatedEffect,
    isLayoutCalculatingStartedEffect,
    isSettingsChangedAction,
    isNodesMovedAction,
    isViewKindChangedAction,
    isSelectionChangedAction,
    isSelectionChangedEffect,
    isHiddenNodesVisibilityChangedAction,
    isHiddenNodesChangedAction,
    isListResultSelectedAction
} from './determine-graph-action';
import {useLayoutAndCameraProps} from './use-layout-and-camera-props';
import {useQueryGraphActions} from './use-query-graph-actions';
import {PresentationChangedAction} from './interface';
import {usePresentationEditedProps} from './use-presentation-edited-props';
import {useGraphSettings} from './use-graph-settings';
import {useGraphSelectionProps} from '../../../../../components/graph/use-graph-selection-props';
import {useHiddenNodesActions} from '../../../../../components/graph/cytoscape-graph/graph-logic/hidden-nodes-actions/use-hidden-nodes-actions';
import {useResultsListProps} from '../result-chains-panel/use-results-list-props';

interface Props {
    graphSettings: ReturnType<typeof useGraphSettings>;
    graphActions: ReturnType<typeof useQueryGraphActions>;
    actionHistoryProps: ReturnType<typeof useActionHistory>;
    layoutAndCameraProps: ReturnType<typeof useLayoutAndCameraProps>;
    presentationEditedProps: ReturnType<typeof usePresentationEditedProps>;
    selectionProps: ReturnType<typeof useGraphSelectionProps>;
    hiddenNodesActionsProps: ReturnType<typeof useHiddenNodesActions>;
    resultsListProps: ReturnType<typeof useResultsListProps>;
}

export function useUndoRedo(props: Props) {
    const {undoQueue, redoQueue, historyMoveBack, historyMoveForward} = props.actionHistoryProps;
    const {
        clearIsCustomLayout,
        setIsCustomLayout,
        visibleNodesPositions,
        setVisibleNodesPositions,
        clearVisibleNodesPositions,
        setLayoutAlgorithm
    } = props.layoutAndCameraProps;
    const {clearIsPresentationEdited} = props.presentationEditedProps;
    const {
        nodeMovedAction,
        rebuildLayoutAction,
        maxPathLengthSelectedAction,
        viewKindToggledAction,
        nodeSelectionChangedAction,
        listResultSelectedAction
    } = props.graphActions;
    const {setMaxPathLength, setViewKind} = props.graphSettings;
    const {setSelectedNodes, setIsRevertingSelChangeEffect} = props.selectionProps;
    const {setHiddenNodesAndMovePositions, toggleIsHiddenNodesVisible} =
        props.hiddenNodesActionsProps;
    const {setSelectedResultItem} = props.resultsListProps;

    const visibleNodePositionsRef = React.useRef(visibleNodesPositions);
    visibleNodePositionsRef.current = visibleNodesPositions;

    const undo = React.useCallback(() => {
        undoQueue.forEach((actionOrEffect) => {
            if (isNodesMovedAction(actionOrEffect)) {
                setVisibleNodesPositions(
                    JSON.stringify({
                        ...visibleNodePositionsRef.current,
                        ...actionOrEffect.oldCoordinates
                    })
                );

                if (actionOrEffect.isEditingLayoutStarted) {
                    clearIsCustomLayout();
                }
            }

            if (isRebuildLayoutAction(actionOrEffect)) {
                setLayoutAlgorithm(actionOrEffect.oldAlgorithm);

                if (actionOrEffect.oldLayoutWasCustom) {
                    setIsCustomLayout('true');
                }
            }

            if (isSettingsChangedAction(actionOrEffect)) {
                setVisibleNodesPositions(JSON.stringify(actionOrEffect.oldLayout));
                setMaxPathLength(actionOrEffect.oldMaxPathLength.toString());
                setViewKind(actionOrEffect.oldViewKind);
            }

            if (isHiddenNodesChangedAction(actionOrEffect)) {
                setHiddenNodesAndMovePositions(actionOrEffect.oldHiddenNodes, false);
            }

            if (isHiddenNodesVisibilityChangedAction(actionOrEffect)) {
                toggleIsHiddenNodesVisible(false);
            }

            if (
                isSelectionChangedAction(actionOrEffect) ||
                isSelectionChangedEffect(actionOrEffect) ||
                isListResultSelectedAction(actionOrEffect)
            ) {
                setSelectedNodes(actionOrEffect.oldSelection);

                if (
                    isSelectionChangedAction(actionOrEffect) ||
                    isListResultSelectedAction(actionOrEffect)
                ) {
                    setSelectedResultItem(actionOrEffect.oldSelectedListResultItem);
                }

                if (isSelectionChangedEffect(actionOrEffect)) {
                    setIsRevertingSelChangeEffect(true);
                }
            }

            if (isLayoutCalculatingStartedEffect(actionOrEffect)) {
                if (actionOrEffect.previousLayout) {
                    setVisibleNodesPositions(JSON.stringify(actionOrEffect.previousLayout));
                } else {
                    clearVisibleNodesPositions();
                }
            }

            if ((actionOrEffect as PresentationChangedAction).isEditingPresentationStarted) {
                clearIsPresentationEdited();
            }

            historyMoveBack();
        });
    }, [
        undoQueue,
        historyMoveBack,
        setVisibleNodesPositions,
        clearIsPresentationEdited,
        clearIsCustomLayout,
        setIsCustomLayout,
        setLayoutAlgorithm,
        clearVisibleNodesPositions,
        setMaxPathLength,
        setViewKind,
        setSelectedNodes,
        setHiddenNodesAndMovePositions,
        toggleIsHiddenNodesVisible,
        setSelectedResultItem,
        setIsRevertingSelChangeEffect
    ]);

    const redo = React.useCallback(() => {
        redoQueue.forEach((actionOrEffect) => {
            if (isNodesMovedAction(actionOrEffect)) {
                nodeMovedAction(
                    {...visibleNodePositionsRef.current, ...actionOrEffect.newCoordinates},
                    true
                );
            }

            if (isRebuildLayoutAction(actionOrEffect)) {
                rebuildLayoutAction(actionOrEffect.newAlgorithm, true);
            }

            if (isSettingsChangedAction(actionOrEffect)) {
                if (isViewKindChangedAction(actionOrEffect)) {
                    viewKindToggledAction(true);
                } else {
                    maxPathLengthSelectedAction(actionOrEffect.newMaxPathLength, true);
                }
            }

            if (
                isSelectionChangedAction(actionOrEffect) ||
                isSelectionChangedEffect(actionOrEffect)
            ) {
                nodeSelectionChangedAction(actionOrEffect.newSelection, false, true);
            }

            if (isListResultSelectedAction(actionOrEffect)) {
                listResultSelectedAction(actionOrEffect.newSelectedListResultItem, true);
            }

            if (isHiddenNodesChangedAction(actionOrEffect)) {
                setHiddenNodesAndMovePositions(actionOrEffect.newHiddenNodes, false);
            }

            if (isHiddenNodesVisibilityChangedAction(actionOrEffect)) {
                toggleIsHiddenNodesVisible(false);
            }

            if (isLayoutCalculatedEffect(actionOrEffect)) {
                setVisibleNodesPositions(JSON.stringify(actionOrEffect.newLayout));
            }

            historyMoveForward();
        });
    }, [
        redoQueue,
        historyMoveForward,
        nodeMovedAction,
        rebuildLayoutAction,
        setVisibleNodesPositions,
        maxPathLengthSelectedAction,
        viewKindToggledAction,
        nodeSelectionChangedAction,
        setHiddenNodesAndMovePositions,
        toggleIsHiddenNodesVisible,
        listResultSelectedAction
    ]);

    return {undo, redo, canUndo: !!undoQueue.length, canRedo: !!redoQueue.length, redoQueue};
}
